// @ts-nocheck

/**
 * MSI3 Test Runner
 * 
 * Comprehensive test runner for all MSI3 patient subscription and revenue features.
 * Provides machine-testable interfaces for automated testing and manual QA.
 * 
 * Usage in browser console:
 *   runMSI3Tests() - Run all tests
 *   runMSI3Tests('revenue') - Run revenue tests only
 *   runMSI3Tests('enrollment') - Run enrollment tests only
 *   runMSI3Tests('subscription') - Run subscription tests only
 */

import { testDataGenerators } from './patientTestService';
import revenueTestUtils, { validateRevenueCalculation, TIER_1_TEST_CASES } from './revenueTestUtils';
import enrollmentTestUtils, { runAllEnrollmentScenarios } from './enrollmentTestUtils';

// ============================================
// Test Suites
// ============================================

/**
 * Revenue Calculation Tests
 */
const runRevenueTests = () => {
  
  const results = {
    name: 'Revenue Calculations',
    tests: [],
    passed: 0,
    failed: 0,
  };

  // Test 1: Revenue share percentage thresholds
  const thresholdTests = [
    { penetration: 0, expected: 75, name: '0% penetration → 75% share' },
    { penetration: 33, expected: 75, name: '33% penetration → 75% share' },
    { penetration: 34, expected: 50, name: '34% penetration → 50% share' },
    { penetration: 66, expected: 50, name: '66% penetration → 50% share' },
    { penetration: 67, expected: 25, name: '67% penetration → 25% share' },
    { penetration: 100, expected: 25, name: '100% penetration → 25% share' },
  ];

  thresholdTests.forEach(test => {
    const actual = revenueTestUtils.calculateRevenueSharePercent(test.penetration);
    const passed = actual === test.expected;
    results.tests.push({ name: test.name, passed, expected: test.expected, actual });
    if (passed) results.passed++; else results.failed++;
  });

  // Test 2: Monthly revenue calculations (Tier 1)
  const revenueValidation = validateRevenueCalculation(TIER_1_TEST_CASES);
  
  revenueValidation.results.forEach(test => {
    results.tests.push({ 
      name: `${test.tier}@${test.revenueSharePct}%`, 
      passed: test.passed, 
      expected: test.expected, 
      actual: test.actual 
    });
    if (test.passed) results.passed++; else results.failed++;
  });

  // Test 3: Penetration calculation
  const penetrationTests = [
    { enrolled: 10000, market: 100000, expected: 10 },
    { enrolled: 44000, market: 440000, expected: 10 },
    { enrolled: 0, market: 100000, expected: 0 },
    { enrolled: 100000, market: 100000, expected: 100 },
  ];

  penetrationTests.forEach(test => {
    const actual = revenueTestUtils.calculatePenetrationPercent(test.enrolled, test.market);
    const passed = Math.abs(actual - test.expected) < 0.01;
    results.tests.push({ name: `${test.enrolled}/${test.market}`, passed, expected: test.expected, actual });
    if (passed) results.passed++; else results.failed++;
  });

  return results;
};

/**
 * Enrollment Flow Tests
 */
const runEnrollmentTests = () => {
  
  const results = {
    name: 'Enrollment Flows',
    tests: [],
    passed: 0,
    failed: 0,
  };

  // Run predefined scenarios
  const scenarioResults = runAllEnrollmentScenarios();
  
  scenarioResults.results.forEach(result => {
    const passed = result.success === true;
    results.tests.push({
      name: result.scenario || result.error || 'Unknown',
      passed,
      result,
    });
    if (passed) results.passed++; else results.failed++;
  });

  // Additional edge case tests

  // Test: Existing enrollment rejection
  const existingEnrollmentTest = enrollmentTestUtils.simulateEnrollmentFlow({
    existingEnrollment: { id: 'existing' },
  });
  const existingTestPassed = !existingEnrollmentTest.success && existingEnrollmentTest.error?.includes('already has');
  results.tests.push({ name: 'Reject duplicate enrollment', passed: existingTestPassed });
  if (existingTestPassed) results.passed++; else results.failed++;

  // Test: Invalid facility code
  const invalidCodeTest = enrollmentTestUtils.simulateEnrollmentFlow({
    facilityCode: 'INVALID_CODE',
    facilityData: null,
  });
  const invalidCodePassed = !invalidCodeTest.success && invalidCodeTest.error?.includes('Invalid');
  results.tests.push({ name: 'Reject invalid facility code', passed: invalidCodePassed });
  if (invalidCodePassed) results.passed++; else results.failed++;

  return results;
};

/**
 * Subscription Tier Change Tests
 */
const runSubscriptionTests = () => {
  
  const results = {
    name: 'Subscription Tiers',
    tests: [],
    passed: 0,
    failed: 0,
  };

  // Create test enrollment
  const testEnrollment = testDataGenerators.generateEnrollment(
    'test-patient-1',
    'test-facility-1',
    'nairobi_001',
    { subscriptionTier: 'free' }
  );

  const testFacility = testDataGenerators.generateFacility('nairobi_001');

  // Test 1: Free to Paid upgrade
  const upgradeResult = enrollmentTestUtils.simulateTierChange({
    enrollment: testEnrollment,
    newTier: 'medium',
    facilityData: testFacility,
    currentPenetration: 20,
  });
  
  const upgradeTests = [
    { name: 'Upgrade succeeds', passed: upgradeResult.success },
    { name: 'Tier changes to medium', passed: upgradeResult.enrollment?.subscriptionTier === 'medium' },
    { name: 'Revenue share becomes active', passed: upgradeResult.enrollment?.revenueShareStatus === 'active' },
    { name: 'Revenue share locked at 75%', passed: upgradeResult.enrollment?.lockedRevenueSharePercentage === 75 },
  ];

  upgradeTests.forEach(test => {
    results.tests.push(test);
    if (test.passed) results.passed++; else results.failed++;
  });

  // Test 2: Paid to Free downgrade
  const paidEnrollment = { ...upgradeResult.enrollment };
  const downgradeResult = enrollmentTestUtils.simulateTierChange({
    enrollment: paidEnrollment,
    newTier: 'free',
  });

  const downgradeTests = [
    { name: 'Downgrade succeeds', passed: downgradeResult.success },
    { name: 'Tier changes to free', passed: downgradeResult.enrollment?.subscriptionTier === 'free' },
    { name: 'Revenue share becomes none', passed: downgradeResult.enrollment?.revenueShareStatus === 'none' },
    { name: 'Current revenue facility cleared', passed: downgradeResult.enrollment?.currentRevenueFacilityId === null },
  ];

  downgradeTests.forEach(test => {
    results.tests.push(test);
    if (test.passed) results.passed++; else results.failed++;
  });

  // Test 3: Paid tier change (preserves locked rate)
  const mediumEnrollment = testDataGenerators.generateEnrollment(
    'test-patient-2',
    'test-facility-1',
    'nairobi_001',
    { 
      subscriptionTier: 'small',
      lockedRevenueSharePercentage: 75,
      revenueShareStatus: 'active',
    }
  );

  const tierChangeResult = enrollmentTestUtils.simulateTierChange({
    enrollment: mediumEnrollment,
    newTier: 'large',
  });

  const tierChangeTests = [
    { name: 'Tier change succeeds', passed: tierChangeResult.success },
    { name: 'Tier changes to large', passed: tierChangeResult.enrollment?.subscriptionTier === 'large' },
    { name: 'Revenue share rate preserved', passed: tierChangeResult.enrollment?.lockedRevenueSharePercentage === 75 },
    { name: 'Monthly amount updated', passed: tierChangeResult.enrollment?.lockedMonthlyAmount > mediumEnrollment.lockedMonthlyAmount },
  ];

  tierChangeTests.forEach(test => {
    results.tests.push(test);
    if (test.passed) results.passed++; else results.failed++;
  });

  return results;
};

/**
 * Clinical Contact Tests
 */
const runClinicalContactTests = () => {
  
  const results = {
    name: 'Clinical Contacts',
    tests: [],
    passed: 0,
    failed: 0,
  };

  // Test 1: Contact updates last contact date
  const enrollment = testDataGenerators.generateEnrollment(
    'test-patient-cc',
    'test-facility-1',
    'nairobi_001',
    { 
      subscriptionTier: 'medium',
      lastClinicalContactDate: new Date('2024-01-01'),
      revenueShareStatus: 'active',
    }
  );

  const contactResult = enrollmentTestUtils.simulateClinicalContact({
    enrollment,
    contactFacilityId: 'test-facility-1',
    isOriginalFacility: true,
  });

  const contactDateUpdated = new Date(contactResult.enrollment?.lastClinicalContactDate) > new Date('2024-01-01');
  results.tests.push({ name: 'Last contact date updated', passed: contactDateUpdated });
  if (contactDateUpdated) results.passed++; else results.failed++;

  // Test 2: Contact restarts paused revenue share
  const pausedEnrollment = testDataGenerators.generateEnrollment(
    'test-patient-paused',
    'test-facility-1',
    'nairobi_001',
    { 
      subscriptionTier: 'medium',
      revenueShareStatus: 'paused',
    }
  );

  const restartResult = enrollmentTestUtils.simulateClinicalContact({
    enrollment: pausedEnrollment,
    contactFacilityId: 'test-facility-1',
    isOriginalFacility: true,
  });

  const restartTests = [
    { name: 'Revenue share restarted', passed: restartResult.revenueShareRestarted === true },
    { name: 'Status becomes active', passed: restartResult.enrollment?.revenueShareStatus === 'active' },
  ];

  restartTests.forEach(test => {
    results.tests.push(test);
    if (test.passed) results.passed++; else results.failed++;
  });

  // Test 3: Contact from different facility doesn't restart
  const noRestartResult = enrollmentTestUtils.simulateClinicalContact({
    enrollment: pausedEnrollment,
    contactFacilityId: 'different-facility',
    isOriginalFacility: false,
  });

  const noRestartPassed = noRestartResult.revenueShareRestarted === false;
  results.tests.push({ name: 'Different facility doesn\'t restart', passed: noRestartPassed });
  if (noRestartPassed) results.passed++; else results.failed++;

  return results;
};

/**
 * Protection Period Tests
 */
const runProtectionTests = () => {
  
  const results = {
    name: 'Protection Periods',
    tests: [],
    passed: 0,
    failed: 0,
  };

  // Test 1: Active protection period
  const recentEnrollment = testDataGenerators.generateEnrollment(
    'test-patient-protected',
    'test-facility-1',
    'nairobi_001',
    { 
      subscriptionTier: 'medium',
      lastClinicalContactDate: new Date(), // Today
      inactivityProtectionMonths: 18,
      revenueShareStatus: 'active',
    }
  );

  const activeResult = enrollmentTestUtils.simulateProtectionCheck({
    enrollment: recentEnrollment,
  });

  const activeProtectionPassed = activeResult.action === 'none';
  results.tests.push({ name: 'Active protection not affected', passed: activeProtectionPassed });
  if (activeProtectionPassed) results.passed++; else results.failed++;

  // Test 2: Expired protection - no contacts → pause
  const expiredDate = new Date();
  expiredDate.setMonth(expiredDate.getMonth() - 20); // 20 months ago

  const expiredEnrollment = testDataGenerators.generateEnrollment(
    'test-patient-expired',
    'test-facility-1',
    'nairobi_001',
    { 
      subscriptionTier: 'medium',
      lastClinicalContactDate: expiredDate,
      inactivityProtectionMonths: 18,
      revenueShareStatus: 'active',
    }
  );

  const pauseResult = enrollmentTestUtils.simulateProtectionCheck({
    enrollment: expiredEnrollment,
    recentContacts: [],
    otherFacilities: [],
  });

  const pauseTests = [
    { name: 'Action is pause', passed: pauseResult.action === 'pause' },
    { name: 'Status becomes paused', passed: pauseResult.enrollment?.revenueShareStatus === 'paused' },
  ];

  pauseTests.forEach(test => {
    results.tests.push(test);
    if (test.passed) results.passed++; else results.failed++;
  });

  // Test 3: Expired protection - other facility contact → transfer
  const otherFacility = testDataGenerators.generateFacility('nairobi_001', {
    id: 'other-facility-1',
    facilityUserId: 'other-facility-user',
  });

  const recentContact = testDataGenerators.generateClinicalContact(
    expiredEnrollment.patientUserId,
    'other-facility-user',
    { contactDate: new Date() }
  );

  const transferResult = enrollmentTestUtils.simulateProtectionCheck({
    enrollment: expiredEnrollment,
    recentContacts: [recentContact],
    otherFacilities: [otherFacility],
  });

  const transferTests = [
    { name: 'Action is transfer', passed: transferResult.action === 'transfer' },
    { name: 'New facility assigned', passed: transferResult.newFacilityId === 'other-facility-1' },
    { name: 'Status remains active', passed: transferResult.enrollment?.revenueShareStatus === 'active' },
  ];

  transferTests.forEach(test => {
    results.tests.push(test);
    if (test.passed) results.passed++; else results.failed++;
  });

  return results;
};

// ============================================
// Main Test Runner
// ============================================

/**
 * Run all MSI3 tests
 * @param {string} suite - Optional: 'revenue', 'enrollment', 'subscription', 'clinical', 'protection'
 */
export const runMSI3Tests = (suite = 'all') => {

  const suites = {
    revenue: runRevenueTests,
    enrollment: runEnrollmentTests,
    subscription: runSubscriptionTests,
    clinical: runClinicalContactTests,
    protection: runProtectionTests,
  };

  let allResults = [];

  if (suite === 'all') {
    allResults = Object.values(suites).map(fn => fn());
  } else if (suites[suite]) {
    allResults = [suites[suite]()];
  } else {
    return null;
  }

  // Summary
  const totalTests = allResults.reduce((sum, r) => sum + r.tests.length, 0);
  const totalPassed = allResults.reduce((sum, r) => sum + r.passed, 0);
  const totalFailed = allResults.reduce((sum, r) => sum + r.failed, 0);


  return {
    suite,
    totalTests,
    totalPassed,
    totalFailed,
    passRate: ((totalPassed / totalTests) * 100).toFixed(1),
    suiteResults: allResults,
  };
};

// ============================================
// Export for window API
// ============================================

if (typeof window !== 'undefined') {
  // Main test runner
  window.runMSI3Tests = runMSI3Tests;
  
  // Individual suite runners
  window.__MSI3_TEST_SUITES__ = {
    runRevenueTests,
    runEnrollmentTests,
    runSubscriptionTests,
    runClinicalContactTests,
    runProtectionTests,
  };

}

export default {
  runMSI3Tests,
  runRevenueTests,
  runEnrollmentTests,
  runSubscriptionTests,
  runClinicalContactTests,
  runProtectionTests,
};

